import { useEffect } from 'react';
import { FeatureToggleType } from '../../featureToggle/featureToggle.constants';
import { useFeatureToggleContext } from '../../featureToggle/useFeatureToggleContext';
import { sendToLogin } from '../../security/auth.helpers';
import { buildAuthLogoutUrl } from '../../utility/location';
import * as SessionStorage from '../../utility/session.storage';
import { SessionTermination } from './SessionTermination';
import type { CloseOtherSessionsMessage } from './sessionTermination.types';

const sessionTermination = new SessionTermination();

type SendToLogin = (loginUrl: string, preventRedirect?: boolean) => void;

/**
 * Keeps this tab aligned with the backend session shared by its peer tabs.
 *
 * Followers remove their tab-scoped state and leave without saving a return
 * URL. The tab that originated the close owns any resume-location update.
 *
 * Windows opened through Open in New Window keep a different session id. They
 * stay signed in unless Close Cloned Windows on Logout is on, in which case
 * they funnel through the logout page with no lastUrl so their own session
 * is closed once, with no resume location.
 */
export function useSessionWatch(
  loginUrl: string,
  termination: Pick<SessionTermination, 'watch'> = sessionTermination,
  leave: SendToLogin = sendToLogin
): void {
  const { isFeatureEnabled } = useFeatureToggleContext();
  const closeClonedSessions = isFeatureEnabled(
    FeatureToggleType.InvCloseClonedSessionsOnLogoutEnabled
  );

  useEffect(() => {
    if (!loginUrl) return undefined;

    const onCloseOtherSessions = closeClonedSessions
      ? (message: CloseOtherSessionsMessage) => {
          const logoutUrl = buildAuthLogoutUrl(loginUrl, {
            reason: message.reason === 'timeout' ? 'timeout' : undefined
          });

          // No React auth logout page: leave the cloned session as it is.
          if (!logoutUrl) return;

          window.location.assign(logoutUrl);
        }
      : undefined;

    return termination.watch(() => {
      try {
        SessionStorage.clear();
      } finally {
        leave(loginUrl, true);
      }
    }, onCloseOtherSessions);
  }, [closeClonedSessions, leave, loginUrl, termination]);
}
